// @flow

import styled from 'styled-components';
import includes from 'lodash/includes';
import moment from 'moment-timezone';
import * as React from 'react';

import { t } from '../lib/i18n';

import type { Campaign } from '../lib/Campaign';
import type { Reengagement, CompletionState } from '../lib/Reengagement';
import { completionStateFor, humanStepNameFromNow, FixedTimeZoneName } from '../lib/Reengagement';

import SmilesIcon from './SmilesIcon';
import LockIcon from './LockIcon';
import MissedIcon from './MissedIcon';
import CompletedIcon from './CompletedIcon';
// import NowIcon from './NowIcon';
import Countdown from './Countdown';


type Props = {
    day: number,
    dayIndexInPlan: number,
    awardedPoints: number,
    isNextStep: boolean,
    campaign: Campaign,
    reengagement: Reengagement,
    className: string,
    onStartChallenge: () => void;
    lastFullSecondTimestamp: number,
}

const icons: { [CompletionState]: ?React.ElementType } = {
    completed: CompletedIcon,
    missed: MissedIcon,
    today: null,
    future: LockIcon,
};


class ReengagementDay extends React.Component<Props> {
    componentWillMount() {

    }

    icon(completionState: CompletionState) {
        const IconComponent = icons[completionState];
        return IconComponent ? <IconComponent /> : null;
    }

    humanTimeString() {
        return humanStepNameFromNow(this.props.reengagement, this.props.day);
    }

    renderHeader(completionState: CompletionState) {
        if (completionState === 'today') {
            const endOfDay: Date = moment
                .tz(this.props.lastFullSecondTimestamp, FixedTimeZoneName)
                .endOf('day')
                .toDate();
            return <Countdown
                date={endOfDay}
                lastFullSecondTimestamp={this.props.lastFullSecondTimestamp}
            />
        }
        return this.humanTimeString()
    }

    renderFooter(completionState: CompletionState) {
        const { awardedPoints } = this.props;
        const buttonCaption = t('reengagement.openAppButtonCaption');

        if (includes(['missed', 'completed', 'future'], completionState)) {
            return (<footer>
                {awardedPoints}
                &nbsp;
                <SmilesIcon />
                &nbsp;
                {t(`reengagement.footer.${completionState}`)}
            </footer>);
        } else if (completionState === 'today') {
            return (<footer>
                <button onClick={this.props.onStartChallenge}>
                    <span>{buttonCaption}</span>
                    <span className="badge">{awardedPoints} <SmilesIcon /></span>
                </button>
            </footer>);
        }
    }

    render() {
        const { day, dayIndexInPlan } = this.props;
        const completionState = completionStateFor(this.props.reengagement, day, dayIndexInPlan);

        const className = [
            'step',
            this.props.isNextStep ? 'next-step' : null,
            this.props.className,
            completionState
        ]
            .filter(Boolean)
            .join(' ');

        return (<section className={className}>
            <header>{this.renderHeader(completionState)}</header>
            <figure>{this.icon(completionState)}</figure>
            {this.renderFooter(completionState)}
        </section>);
    }
}

export default styled(ReengagementDay)`
    display: flex;
    flex-direction: row;

    &.future {
        > * {
            opacity: 0.75;
        }
    }

    &.missed {
        color: #ec637f;
        svg g, svg path, svg polygon {
            fill: #ec637f;
        }
    }

    &.completed {
        color: #0fd4a0;
        svg g, svg path, svg polygon {
            fill: #0fd4a0;
        }
    }

    &.today.next-step {
        color: #17b3ee;
        background-color: #eff8ff;
        svg g, svg path, svg polygon {
            fill: #17b3ee;
        }
    }

    > * {
        display: flex;
        align-items: center;
        box-sizing: border-box;
    }

    > header, > footer {
        height: 60px;
        padding: 10px;
    }

    > header {
        flex: 2;
        justify-content: flex-end;
        text-align: right;
    }

    > footer {
        flex: 3;
        button {
            pointer-events: all;
            display: flex;
            justify-content: space-between;
            justify-content: space-evenly;
            max-width: 15em;
            font-weight: bold;
            height: 50px;
            width: 100%;
            padding: 6px;
            border: none;
            border-radius: 0;
            font-size: 1rem;
            color: white;
            background-color: #17b3ee;
            .badge {
                font-weight: normal;
                opacity: 0.8;
                .smiles-icon path {
                    fill: white;
                }
                margin-left: 1em;
            }
        }
    }

    > figure {
        width: 40px;
        height: 60px;
        display: flex;
        align-items: center;
        justify-content: center;
    }
`;



// WEBPACK FOOTER //
// ./src/Reengagement/ReengagementDay.jsx